<template>
  <div class="py-5 md:px-5">
    <PageHeader title="Reports" class="container">
      <button class="btn btn-primary" @click="showNewReportModal = true">
        New Report
      </button>
    </PageHeader>

    <Modal title="New Report" :open.sync="showNewReportModal" size="max-w-4xl">
      <div v-for="section in filteredSections" :key="section.name" class="p-6">
        <div class="mb-4 text-sm font-medium tracking-wide text-gray-500 uppercase">
          {{ section.name }}
        </div>
        <div class="grid grid-cols-2 gap-4">
          <router-link class="p-5 text-gray-800 border rounded hover:bg-gray-50 hover:border-gray-300 hover:no-underline" v-for="report in section.reports" :key="report.type" :to="{ name: 'ReportView', params: { type: report.type } }">
            <div class="flex items-center leading-tight">
              <fa :icon="['fas', report.icon]" class="mr-2 text-sm text-gray-500" />
              <div class="font-medium">
                {{ report.name }}
              </div>
            </div>   
            <div class="mt-2 text-sm text-gray-500">
              {{ report.description }}
            </div>
          </router-link>
        </div>
      </div>
    </Modal>

    <div class="container">
      <!-- List -->
      <div class="card">
        <div class="flex flex-col justify-between p-4 border-b md:flex-row lg:p-5">
          <div class="flex flex-col space-y-3 md:space-y-0 md:flex-row md:space-x-3">
            <filter-dropdown @apply="applyFilter" @clear="clearFilter" :loading="loading">
              <div>
                <label for="type" class="form-label">Type</label>
                <report-type-select type="type" v-model="filter.type" />
              </div>
            </filter-dropdown>
          </div>
        </div>

        <!-- Loading -->
        <Loading v-if="loading" class="p-6" />

        <!-- No Results -->
        <div v-else-if="reports.meta.total < 1" class="max-w-sm py-12 mx-auto text-center">
          <div class="mb-2 text-lg font-bold">
            No reports setup yet.
          </div>
          <div class="text-gray-500">
            You can save reports with preset options so you can easily pull them again later or schedule to be sent via email at regular intervals.
          </div>
          <button class="mt-4 btn btn-outline" @click="showNewReportModal = true">
            New Report
          </button>
        </div>

        <!-- Listing -->
        <div class="divide-y" v-else>
          <div v-for="report in reports.data" :key="report.id">
            <router-link :to="{ name: 'ReportEdit', params: { id: report.id } }" class="flex items-center flex-grow p-4 text-gray-900 lg:px-6 lg:py-4 hover:no-underline hover:bg-gray-50">
              <div>
                <!-- Name -->
                <div class="font-bold leading-tight font-weight-bold">
                  {{ report.name }}
                </div>
                <!-- Options -->
                <div class="grid grid-flow-col gap-1" v-if="report.scheduled">
                  <div class="text-gray-600">
                    {{ report.type.replaceAll('-', ' ') | capitalize }}
                  </div>
                  <div class="mx-1 text-gray-600">
                    &bullet;
                  </div>
                  <div class="text-gray-600">
                    Every {{ report.recurrence }} {{ report.recurrence | pluralize(report.frequency) }} at {{ report.time_of_day.substr(0, 5) }} 
                    <span v-if="report.frequency !== 'day'" class="pl-1">
                      on {{ report.frequency === 'week' ? report.day_of_week : report.day_of_month }}
                    </span>
                  </div>
                </div>
                <div v-else>
                  <div class="text-gray-600">
                    Not Scheduled
                  </div>
                </div>
              </div>
            </router-link>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import FilterDropdown from '@/components/FilterDropdown'
import ReportTypeSelect from '@/components/ReportTypeSelect'

export default {
  name: 'ReportList',

  metaInfo: {
    title: 'Reports'
  },

  components: {
    FilterDropdown,
    ReportTypeSelect
  },

  computed: {
    ...mapState(['reportTypes']),
    ...mapGetters(['account']),

    filteredSections () {
      const sections = JSON.parse(JSON.stringify(this.reportTypes.sections))
      
      const filterReports = (section) => {
        const reports = []
        section.reports.map((report) => {
          // If there are no required products or integrations (i.e. length === 0)
          // Then we don't need to check them as a requirement, so just mark them as true
          let hasAtLeastOneProduct = report.products.length === 0 ? true : false
          let hasAtLeastOneIntegration = report.integrations.length === 0 ? true : false

          for (let product of report.products) {
            if (this.account.subscription[product]) {
              hasAtLeastOneProduct = true
            }
          }

          for (let integration of report.integrations) {
            if (this.account[`integration_${integration}`]) {
              hasAtLeastOneIntegration = true
            }
          }

          if (hasAtLeastOneProduct && hasAtLeastOneIntegration) {
            reports.push(report)
          }
        })
        return reports
      }

      const filteredSections = sections.map((section) => {
        return {
          ...section,
          reports: filterReports(section)
        }
      })

      return filteredSections.filter((section) => section.reports.length > 0)
    }
  },

  created () {
    this.setFilter()
    this.getReports()
  },

  data () {
    return {
      showNewReportModal: false,
      loading: true,
      reports: {},
      filter: {
        type: null
      }
    }
  },

  methods: {
    async getReports () {
      try {
        this.loading = true
        const { data } = await this.$api.get(`account/${this.$route.params.account}/reports`, {
          params: {
            page: this.$route.query.page || 1,
            sort: this.$route.query.sort || 'name',
            dir: this.$route.query.dir || 'asc',
            ...this.filter && { filter: this.filter }
          }
        })
        this.reports = data.reports
        this.loading = false
      } catch (error) {
        console.error(error)
      }
    },

    setFilter () {
      if (this.$route.query.filter) {
        this.filter = JSON.parse(this.$route.query.filter)
      }
    },

    applyFilter () {
      this.$router.push({ 
        query: { 
          ...this.$route.query, 
          filter: JSON.stringify(this.filter)
        } 
      })
    },

    clearFilter () {
      this.filter = {
        type: null
      }
      this.$router.push({ 
        query: { 
          ...this.$route.query, 
          filter: undefined
        } 
      })
    }
  },

   watch: {
    '$route.query': {
      handler (newVal, oldVal) {
        this.getReports()
      },
      deep: true
    }
  }
}
</script>